import { useEffect, useState } from "react";
import {
  useGetMyStatusQuery,
  usePatchMySettingsMutation,
  usePutMyBinanceKeysMutation,
  useDeleteMyBinanceKeysMutation,
  useGetMyFeesQuery,
} from "@/store/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert } from "@/components/ui/alert";
import { toast } from "sonner";
import { Save, KeyRound, Trash2 } from "lucide-react";

export default function UserSettings() {
  const { data: status, isLoading, refetch } = useGetMyStatusQuery(undefined, {
    pollingInterval: 30000,
  });
  const fees = useGetMyFeesQuery();
  const [patchSettings, { isLoading: saving }] = usePatchMySettingsMutation();
  const [putKeys, { isLoading: savingKeys }] = usePutMyBinanceKeysMutation();
  const [deleteKeys, { isLoading: deletingKeys }] = useDeleteMyBinanceKeysMutation();

  const [perOrder, setPerOrder] = useState<number>(10);
  const [maxOrders, setMaxOrders] = useState<number>(5);
  const [approvalMode, setApprovalMode] = useState<"manual" | "auto">("manual");
  const [paused, setPaused] = useState<boolean>(false);
  const [startingEquity, setStartingEquity] = useState<string>("");
  const [personalize, setPersonalize] = useState<boolean>(false);
  const [dryRun, setDryRun] = useState<boolean>(true);
  const [dcaEnabled, setDcaEnabled] = useState<boolean>(false);
  const [dcaAmountOverride, setDcaAmountOverride] = useState<string>("");

  const [apiKey, setApiKey] = useState("");
  const [apiSecret, setApiSecret] = useState("");

  // Hydrate when /api/me/status returns
  useEffect(() => {
    if (status?.settings) {
      setPerOrder(Number(status.settings.per_order_usd ?? 10));
      setMaxOrders(Number(status.settings.max_orders_per_day ?? 5));
      setApprovalMode((status.settings.approval_mode as "manual" | "auto") ?? "manual");
      setPaused(Boolean(status.settings.paused));
      setStartingEquity(
        status.settings.starting_equity_usd != null
          ? String(status.settings.starting_equity_usd)
          : ""
      );
      setPersonalize(Boolean(status.settings.personalize_risk_review));
      setDryRun(status.settings.dry_run !== false);
      setDcaEnabled(Boolean((status.settings as any).dca_enabled));
      const o = (status.settings as any).dca_amount_override_usd;
      setDcaAmountOverride(o != null ? String(o) : "");
    }
  }, [status]);

  const onSaveSettings = async () => {
    try {
      await patchSettings({
        per_order_usd: Number(perOrder),
        max_orders_per_day: Number(maxOrders),
        approval_mode: approvalMode,
        paused: paused ? 1 : 0,
        starting_equity_usd: startingEquity === "" ? null : Number(startingEquity),
        personalize_risk_review: personalize ? 1 : 0,
        dry_run: dryRun ? 1 : 0,
        dca_enabled: dcaEnabled ? 1 : 0,
        dca_amount_override_usd: dcaAmountOverride === "" ? null : Number(dcaAmountOverride),
      }).unwrap();
      toast.success("Settings saved");
      refetch();
    } catch (e) {
      const msg = (e as { data?: { detail?: string } })?.data?.detail ?? "Save failed";
      toast.error(msg);
    }
  };

  const onSaveKeys = async () => {
    if (!apiKey || !apiSecret) return toast.error("API key and secret required");
    try {
      const res = await putKeys({
        api_key: apiKey.trim(), api_secret: apiSecret.trim(),
      }).unwrap();
      if (res.cycle_triggered && res.cycle_id) {
        toast.success(
          `Binance keys saved + validated. Cycle #${res.cycle_id} just kicked off — your first user_cycles row appears in a few seconds.`,
        );
      } else {
        toast.success("Binance keys saved + validated");
      }
      setApiKey("");
      setApiSecret("");
      refetch();
    } catch (e) {
      const msg = (e as { data?: { detail?: string } })?.data?.detail ?? "Key save failed";
      toast.error(msg);
    }
  };

  const onDeleteKeys = async () => {
    if (!confirm("Remove your Binance keys? The bot will stop trading on your account.")) return;
    try {
      await deleteKeys().unwrap();
      toast.success("Keys removed");
      refetch();
    } catch (e) {
      const msg = (e as { data?: { detail?: string } })?.data?.detail ?? "Delete failed";
      toast.error(msg);
    }
  };

  if (isLoading || !status) return <Skeleton className="h-96 w-full" />;

  const hasKeys = !!status.has_keys;
  const feeSummary = fees.data?.summary;

  return (
    <div className="space-y-4 max-w-3xl">
      <Card>
        <CardHeader>
          <CardTitle>
            Your account
            <span className="text-xs text-muted ml-2 font-normal">{status.user.email}</span>
          </CardTitle>
          <CardDescription>
            Set your Binance API key + per-order limits. The strategy is
            shared (managed by the operator) — you control how big your
            orders get and how many can run per day.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-2">
            <Badge tone={hasKeys ? "good" : "warn"}>
              {hasKeys ? "API keys on file" : "No API keys yet"}
            </Badge>
            <Badge tone={paused ? "warn" : "good"}>{paused ? "paused" : "active"}</Badge>
            <Badge tone="info">{approvalMode}</Badge>
            {feeSummary && (
              <Badge tone="muted">
                10% fee accrued: ${feeSummary.outstanding_fee.toFixed(2)} outstanding
              </Badge>
            )}
          </div>
          {!hasKeys && (
            <Alert tone="warn">
              The bot will not place orders on your account until you save valid
              Binance API keys below. Use a key restricted to <strong>spot trading</strong>;
              do <strong>not</strong> grant withdraw permission.
            </Alert>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            <KeyRound className="inline w-4 h-4 mr-2" />
            Binance API keys
          </CardTitle>
          <CardDescription>
            Stored encrypted at rest. We validate the key by calling
            fetch_balance on save — bad keys are rejected before they're stored.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-[160px_1fr] items-center gap-2">
            <Label>API key</Label>
            <Input
              type="text"
              placeholder={hasKeys ? "•••••• (already on file — paste to replace)" : "Paste API key"}
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              autoComplete="off"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-[160px_1fr] items-center gap-2">
            <Label>API secret</Label>
            <Input
              type="password"
              placeholder={hasKeys ? "•••••• (already on file — paste to replace)" : "Paste API secret"}
              value={apiSecret}
              onChange={(e) => setApiSecret(e.target.value)}
              autoComplete="new-password"
            />
          </div>
          <div className="flex gap-2">
            <Button
              variant="primary"
              size="sm"
              disabled={savingKeys || (!apiKey || !apiSecret)}
              onClick={onSaveKeys}
            >
              {savingKeys ? "Validating…" : "Save + validate"}
            </Button>
            {hasKeys && (
              <Button variant="ghost" size="sm" disabled={deletingKeys} onClick={onDeleteKeys}>
                <Trash2 className="w-3.5 h-3.5" /> Remove keys
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            Dollar-cost average (DCA)
            <Badge tone={dcaEnabled ? "good" : "muted"} className="ml-2">
              {dcaEnabled ? "on" : "off"}
            </Badge>
          </CardTitle>
          <CardDescription>
            Optional. Buys a fixed USD slice across the operator-configured
            symbols (default BTC/ETH/BNB) on a fixed cadence (default 24h).
            Runs independently of cycles + scalp — won't pause during
            auto-defense. Uses your own Binance keys.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-3">
            <button type="button" onClick={() => setDcaEnabled(!dcaEnabled)}
                    className={
                      "px-3 py-1 text-xs rounded-md border " +
                      (dcaEnabled
                        ? "bg-bg2 border-accent text-fg"
                        : "border-border text-muted hover:text-fg")
                    }>
              {dcaEnabled ? "Disable" : "Enable"}
            </button>
            <span className="text-xs text-muted">
              You can leave this on while everything else is paused.
            </span>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label>Amount override (USD per run)</Label>
              <Input type="number" step="1" min="0"
                      value={dcaAmountOverride}
                      onChange={(e) => setDcaAmountOverride(e.target.value)}
                      placeholder="leave empty to use the operator's default" />
              <p className="text-xs text-muted mt-1">
                Empty = use the global default. Min $5 per symbol after the
                amount is split (e.g. $15 for 3 symbols).
              </p>
            </div>
          </div>
          <Button size="sm" variant="primary" disabled={saving}
                  onClick={onSaveSettings}>
            <Save className="w-3.5 h-3.5" /> Save DCA settings
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Order limits</CardTitle>
          <CardDescription>
            These are the only knobs you control. The strategy itself
            (whitelist, schedule, risk caps, advisor) is managed centrally
            and the same for everyone.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-[260px_1fr] items-center gap-2">
            <Label>Per-order USD (cap)</Label>
            <Input
              type="number"
              step="0.5"
              value={String(perOrder)}
              onChange={(e) => setPerOrder(parseFloat(e.target.value || "0"))}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-[260px_1fr] items-center gap-2">
            <Label>Max orders / day</Label>
            <Input
              type="number"
              step="1"
              value={String(maxOrders)}
              onChange={(e) => setMaxOrders(parseInt(e.target.value || "0", 10))}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-[260px_1fr] items-center gap-2">
            <Label>Approval mode</Label>
            <select
              className="h-9 rounded-md border border-border bg-bg px-3 text-sm"
              value={approvalMode}
              onChange={(e) => setApprovalMode(e.target.value as "manual" | "auto")}
            >
              <option value="manual">Manual — I approve each trade</option>
              <option value="auto">Auto — execute on my account immediately</option>
            </select>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-[260px_1fr] items-center gap-2">
            <Label>Pause my account</Label>
            <input
              type="checkbox"
              className="h-4 w-4 accent-accent"
              checked={paused}
              onChange={(e) => setPaused(e.target.checked)}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-[260px_1fr] items-center gap-2">
            <Label>Starting equity (for ROI %)</Label>
            <Input
              type="number"
              step="1"
              placeholder="e.g. 1000"
              value={startingEquity}
              onChange={(e) => setStartingEquity(e.target.value)}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-[260px_1fr] items-start gap-2">
            <div>
              <Label>Dry run (paper trading)</Label>
              <p className="text-xs text-muted mt-0.5">
                When ON, every order the bot places for you is a simulated
                fill — no real Binance trade. Use this to validate behaviour
                before you go live. Note: if the admin has the global
                dry-run flag ON, you will be dry-run regardless.
              </p>
            </div>
            <input
              type="checkbox"
              className="h-4 w-4 accent-accent mt-1.5"
              checked={dryRun}
              onChange={(e) => setDryRun(e.target.checked)}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-[260px_1fr] items-start gap-2">
            <div>
              <Label>Personal AI risk review</Label>
              <p className="text-xs text-muted mt-0.5">
                Adds one Gemini Flash check per cycle on your account.
                Can downgrade a proposed trade to HOLD or tighten the
                size — never loosens. Optional.
              </p>
            </div>
            <input
              type="checkbox"
              className="h-4 w-4 accent-accent mt-1.5"
              checked={personalize}
              onChange={(e) => setPersonalize(e.target.checked)}
            />
          </div>
          <div className="pt-2">
            <Button variant="primary" size="sm" disabled={saving} onClick={onSaveSettings}>
              <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save settings"}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Profit-share — 10% of monthly net profit</CardTitle>
          <CardDescription>
            Charged on each calendar month's NET realized P&L (wins minus
            losses). Losing months cost zero performance fee. Per-close
            details remain visible in your Trades tab.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {fees.isLoading || !feeSummary ? (
            <Skeleton className="h-16 w-full" />
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
              <div>
                <div className="text-xs text-muted uppercase tracking-wide">Outstanding</div>
                <div className="text-lg font-semibold">${feeSummary.outstanding_fee.toFixed(2)}</div>
              </div>
              <div>
                <div className="text-xs text-muted uppercase tracking-wide">Paid to date</div>
                <div className="text-lg font-semibold">${feeSummary.paid_fee.toFixed(2)}</div>
              </div>
              <div>
                <div className="text-xs text-muted uppercase tracking-wide">Months tracked</div>
                <div className="text-lg font-semibold">{feeSummary.n_periods ?? feeSummary.n_accruals ?? 0}</div>
              </div>
              <div>
                <div className="text-xs text-muted uppercase tracking-wide">Lifetime net</div>
                <div className={"text-lg font-semibold " +
                  ((feeSummary.lifetime_net_pnl_usd ?? 0) >= 0 ? "text-good" : "text-bad")}>
                  ${(feeSummary.lifetime_net_pnl_usd ?? 0).toFixed(2)}
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
